import { useEffect, useState } from "react";
import { COUNTRY_CODES, buildPhone, parsePhone, sanitizePhoneNumber } from "../utils/phone";

// Splits a stored phone value into a country code + number pair. The selected code is
// kept locally so it survives while the number is still empty.
export function usePhoneParts(value, onChange) {
  const parsed = parsePhone(value);
  const [dial, setDial] = useState(parsed.dial);

  useEffect(() => {
    if (value) setDial(parsePhone(value).dial);
  }, [value]);

  const number = value ? parsed.number : "";

  const changeDial = (nextDial) => {
    setDial(nextDial);
    onChange(buildPhone(nextDial, sanitizePhoneNumber(nextDial, number)));
  };
  const changeNumber = (raw) => onChange(buildPhone(dial, sanitizePhoneNumber(dial, raw)));

  return { dial, number, changeDial, changeNumber };
}

export function CountryCodeSelect({ value, onChange, disabled, className = "" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label="Country code"
      className={`cursor-pointer outline-none disabled:cursor-not-allowed ${className}`}
    >
      {COUNTRY_CODES.map((c) => (
        <option key={c.iso} value={c.dial} title={c.name}>
          {c.iso} +{c.dial}
        </option>
      ))}
    </select>
  );
}

// Same look as TextInput, with a country code picker in front of the number.
// onChange receives the full value ("+919876543210"), not an event.
export default function PhoneInput({
  label,
  value,
  onChange,
  onBlur,
  error,
  placeholder = "Phone number",
  required,
  disabled,
}) {
  const { dial, number, changeDial, changeNumber } = usePhoneParts(value, onChange);

  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        // Only counts as a blur once focus leaves both the code picker and the number
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) onBlur?.(e);
        }}
        className={`flex h-9 w-full overflow-hidden rounded-md border transition-colors focus-within:ring-2 ${
          disabled ? "cursor-not-allowed bg-slate-50 text-slate-400" : "bg-white"
        } ${
          error
            ? "border-red-300 focus-within:border-red-400 focus-within:ring-red-100"
            : "border-slate-300 focus-within:border-brand-500 focus-within:ring-brand-500/20"
        }`}
      >
        <CountryCodeSelect
          value={dial}
          onChange={changeDial}
          disabled={disabled}
          className="border-r border-slate-200 bg-slate-50 px-2 text-[13px] text-slate-700"
        />
        <input
          type="tel"
          inputMode="numeric"
          value={number}
          onChange={(e) => changeNumber(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 bg-transparent px-3 text-[13px] outline-none disabled:cursor-not-allowed"
        />
      </div>
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
    </div>
  );
}
